import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  BentoCategory,
  FollowUpQuestionOption,
  NicheFollowUpQuestion,
  OrganizationNiche,
  OrganizationNicheStatus,
  TemplateError,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { LANGUAGES } from "schemas/languages";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import QuestionChip from "components/Styled/QuestionChip";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import StatusChip from "../StatusChip";
import EditNicheTemplate from "./EditNicheTemplate";

const styles = {
  drawer: {
    "& .MuiDrawer-paper": {
      position: "absolute",
      right: "-8px",
      boxShadow: "-8px 0px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  editIcon: {
    fontSize: 14,
    color: grey[500],
  },
  closeIcon: {
    position: "absolute",
    right: "18px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
  link: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: "primary.main",
      textDecoration: "underline",
    },
  },
  locationAutocomplete: {
    border: `1px solid ${grey[400]}`,
    borderRadius: "8px",
    width: { xs: "100%", md: "80%", lg: "80%" },
    height: 44,
    py: 0,
    px: 2,
    fontFamily: "'Inter', sans-serif",
    "&:hover, &:focus": {
      outline: "none",
    },
    fontSize: 14,
    "::placeholder": {
      color: grey[500],
    },
    mb: 2,
  },
};

interface IProps {
  handleClose: (e?: CustomEvent) => void;
  editNiche: (niche: OrganizationNiche) => void;
  removeNiche: (niche: OrganizationNiche) => void;
  selectedNiche: OrganizationNiche | null;
  templates: Template[] | null;
  templateDelayDays?: (number | null)[] | null;
  categoriesToPickFrom: BentoCategory[];
  loadData?: () => void;
}

export default function EditNicheDrawer({
  handleClose,
  editNiche,
  removeNiche,
  selectedNiche,
  templates,
  templateDelayDays,
  categoriesToPickFrom,
  loadData,
}: IProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [loadingFollowUpQuestions, setLoadingFollowUpQuestions] =
    useState<boolean>(false);
  const { templates: allTemplates } = useContext(OutreachTemplatesContext);
  const [followUpQuestions, setFollowUpQuestions] = useState<
    NicheFollowUpQuestion[] | null
  >(null);
  const [selectedFollowupAnswers, setSelectedFollowupAnswers] = useState<{
    [key in number]: FollowUpQuestionOption[];
  }>({});
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [modifiedTemplateDelayDays, setModifiedTemplateDelayDays] = useState<
    (number | null)[]
  >([]);
  const [followUpError, setFollowUpError] = useState<boolean>(false);
  const [warningDialog, setWarningDialog] = useState<boolean>(false);
  const [cancelEmailsLoading, setCancelEmailsLoading] =
    useState<boolean>(false);
  const [canceledNicheId, setCanceledNicheId] = useState<number | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [nicheLocation, setNicheLocation] = useState<
    UserLocation | undefined
  >();
  const [locationError, setLocationError] = useState<
    "city" | "location" | null
  >(null);

  const fetchFollowUpQuestions = async () => {
    setLoadingFollowUpQuestions(true);
    try {
      const { followUpQuestions } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/${selectedNiche?.bentoCategoryId}/follow-up-questions`,
        "GET",
      );
      setFollowUpQuestions(followUpQuestions);
      await fetchFollowUpResponses();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingFollowUpQuestions(false);
    }
  };

  const fetchFollowUpResponses = async () => {
    setLoadingFollowUpQuestions(true);
    try {
      const { followUpResponses } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${selectedNiche?.id}/follow-up-responses`,
        "GET",
      );
      setSelectedFollowupAnswers(followUpResponses);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingFollowUpQuestions(false);
    }
  };

  const optionIsSelected = (
    followUpQuestion: NicheFollowUpQuestion,
    option: FollowUpQuestionOption,
  ) => {
    return selectedFollowupAnswers[followUpQuestion.id]?.some(
      (selectedOption) => selectedOption.option === option.option,
    );
  };

  // Drop any answers whose question doesn't belong to this niche's category.
  // Prevents state from a previous niche/drawer session being POSTed back.
  const scopeFollowUpResponses = () => {
    const validQuestionIds = new Set(followUpQuestions?.map((q) => q.id) ?? []);
    return Object.fromEntries(
      Object.entries(selectedFollowupAnswers).filter(([qid]) =>
        validQuestionIds.has(Number(qid)),
      ),
    );
  };

  const deleteNiche = async () => {
    setDeleteLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${selectedNiche?.id}/with-automations`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates.map((template) => template.id),
          templateDelayDays: modifiedTemplateDelayDays,
          followUpResponses: scopeFollowUpResponses(),
          isArchived: true,
        },
      );

      if (selectedNiche) {
        removeNiche(selectedNiche);
        setCanceledNicheId(selectedNiche.id ?? null);
      }
      setWarningDialog(true);
      onClose();
    } catch (error) {
      if (error) setErrorAlert(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const saveNiche = async (status?: OrganizationNicheStatus) => {
    if (status) {
      setStatusLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${selectedNiche?.id}/with-automations`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates.map((template) => template.id),
          templateDelayDays: modifiedTemplateDelayDays,
          followUpResponses: scopeFollowUpResponses(),
          status,
          nicheLocation,
          languages: selectedLanguages,
        },
        true,
      );
      const jsonMessage = await res.json();

      if (res.ok) {
        const { niche } = jsonMessage;
        editNiche(niche);
        if (
          niche.status === OrganizationNicheStatus.canceled &&
          status === OrganizationNicheStatus.canceled
        ) {
          setCanceledNicheId(niche.id);
          setWarningDialog(true);
        }
        onClose();
      } else {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }

          if (
            jsonMessage.field === "city" ||
            jsonMessage.field === "location"
          ) {
            setLocationError(jsonMessage.field);
            return;
          }

          if (jsonMessage.field === "followUpQuestions") {
            setFollowUpError(true);
            return;
          }
        }
        setAlert(jsonMessage.message, "error");
      }
    } catch (error) {
      if (error) setErrorAlert(error);
    } finally {
      if (status) {
        setStatusLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const cancelScheduledEmails = async () => {
    if (!canceledNicheId || cancelEmailsLoading) return;
    setCancelEmailsLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${canceledNicheId}/cancel-scheduled-emails`,
        "POST",
      );
      setAlert(
        "Scheduled emails for this niche have been cancelled",
        "success",
      );
      setWarningDialog(false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCancelEmailsLoading(false);
    }
  };

  const initializeLocation = () => {
    if (selectedNiche?.countryCode) {
      setNicheLocation({
        address: selectedNiche?.city,
        longitude: selectedNiche?.longitude,
        latitude: selectedNiche?.latitude,
        country_code: selectedNiche?.countryCode,
        address_components: selectedNiche?.addressComponents,
      });
    } else if (profile?.location) {
      setNicheLocation({
        address: profile?.city,
        longitude: profile?.longitude,
        latitude: profile?.latitude,
        country_code: profile?.location,
        address_components: profile?.addressComponents,
      });
    }
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    if (userLocation.country_code) {
      setLocationError(null);
    }
    setNicheLocation(userLocation);
  };

  useEffect(() => {
    // Clear stale state from a previously-selected niche before the new fetch lands.
    setSelectedFollowupAnswers({});
    setFollowUpQuestions(null);
  }, [selectedNiche?.id]);

  useEffect(() => {
    if (selectedNiche?.bentoCategoryId) {
      fetchFollowUpQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, selectedNiche?.id]);

  useEffect(() => {
    if (selectedNiche?.id) {
      initializeLocation();
      setSelectedLanguages(selectedNiche.languages || []);
      setModifiedTemplateDelayDays(templateDelayDays || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNiche?.id, templateDelayDays]);

  const onClose = () => {
    setFollowUpQuestions(null);
    setSelectedFollowupAnswers({});
    setTemplatesError(null);
    setFollowUpError(false);
    setModifiedTemplates([]);
    setModifiedTemplateDelayDays([]);
    setSelectedLanguages([]);
    setNicheLocation(undefined);
    setLocationError(null);
    handleClose();
  };

  const showEditLocation = useMemo(() => {
    if (!selectedNiche?.bentoCategoryId) {
      return false;
    }
    if (
      categoriesToPickFrom.some((c) => c.id === selectedNiche?.bentoCategoryId)
    ) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesToPickFrom.length, selectedNiche?.bentoCategoryId]);

  return (
    <>
      <Drawer
        open={!!selectedNiche}
        anchor="right"
        onClose={onClose}
        sx={styles.drawer}
      >
        <Box
          sx={{
            p: 4,
            minWidth: "30vw",
            maxWidth: "500px",
          }}
        >
          <Typography variant="h4" sx={{ mr: 4, mt: 2 }}>
            {selectedNiche?.name}{" "}
            {selectedNiche?.status && (
              <StatusChip status={selectedNiche.status} />
            )}
          </Typography>
          <IconButton sx={styles.closeIcon} onClick={onClose}>
            <Box component="span" className="fa-regular fa-xmark-large" />
          </IconButton>
          <Divider sx={{ mt: 2, mb: 4 }} />

          <EditNicheTemplate
            modifiedTemplates={modifiedTemplates}
            setModifiedTemplates={setModifiedTemplates}
            modifiedTemplateDelayDays={modifiedTemplateDelayDays}
            setModifiedTemplateDelayDays={setModifiedTemplateDelayDays}
            templatesError={templatesError}
            nicheTemplates={templates || []}
            niche={selectedNiche!}
            loadData={loadData}
          />

          {loadingFollowUpQuestions && (
            <Grid container sx={{ mt: -2, pb: 2 }}>
              <Grid item container xs={12}>
                <Grid item xs={12}>
                  <Skeleton height="50px" />
                </Grid>
                <Grid item container wrap="wrap" gap={1} xs={12}>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                </Grid>
                <Grid item container wrap="wrap" gap={1} xs={12}>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                  <Grid item xs>
                    <Skeleton height="50px" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {!loadingFollowUpQuestions &&
            followUpQuestions?.map((followUpQuestion) => (
              <Box key={followUpQuestion.id} sx={{ mb: 4 }}>
                <Box>
                  <Typography variant="h6">
                    {followUpQuestion.question}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" paragraph>
                    {followUpQuestion.description}
                  </Typography>
                </Box>
                {followUpError && followUpQuestion.isRequired && (
                  <Box sx={{ mb: 2, mt: -2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: "italic",

                        color: "error.main",
                      }}
                    >
                      This question is required. Please select at least one
                      option below.
                    </Typography>
                  </Box>
                )}
                <Box display="flex" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
                  {followUpQuestion.options.map(
                    (option: FollowUpQuestionOption) => {
                      const isSelected = optionIsSelected(
                        followUpQuestion,
                        option,
                      );
                      return (
                        <QuestionChip
                          isSelected={isSelected}
                          onClick={() => {
                            setFollowUpError(false);
                            if (isSelected) {
                              setSelectedFollowupAnswers((prev) => {
                                const copy = { ...prev };
                                copy[followUpQuestion.id] = copy[
                                  followUpQuestion.id
                                ].filter((v) => v.option !== option.option);
                                return copy;
                              });
                            } else {
                              setSelectedFollowupAnswers((prev) => {
                                const copy = { ...prev };
                                copy[followUpQuestion.id] = [
                                  ...(copy[followUpQuestion.id] || []),
                                  option,
                                ];
                                return copy;
                              });
                            }
                          }}
                          key={option.option}
                          label={option.option}
                        />
                      );
                    },
                  )}
                </Box>
              </Box>
            ))}
          {showEditLocation && (
            <>
              <Box sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">Location Priority</Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ maxWidth: "80%" }}
                    paragraph
                  >
                    Brands in this city will be prioritized if location is
                    relevant. Otherwise, we will pitch more widely.
                  </Typography>
                </Box>
                {!!locationError && (
                  <Box sx={{ mt: -1, mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: "italic", color: "error.main" }}
                    >
                      {locationError === "city" ? (
                        <>
                          Please enter a <strong>city</strong> to save this
                          niche
                        </>
                      ) : (
                        <>Please enter a location to save this niche</>
                      )}
                    </Typography>
                  </Box>
                )}
                <LocationAutocomplete
                  key={selectedNiche?.id}
                  handleLocationChange={handleLocationChange}
                  customStyling={styles.locationAutocomplete}
                  userLocation={nicheLocation}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">Language Preference</Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ maxWidth: "80%" }}
                    paragraph
                  >
                    Only brands that communicate in these languages will be
                    pitched. Leave empty to pitch in all languages.
                  </Typography>
                </Box>
                <Autocomplete
                  multiple
                  options={LANGUAGES}
                  getOptionLabel={(option) => option.name}
                  value={LANGUAGES.filter((l) =>
                    selectedLanguages.includes(l.key),
                  )}
                  onChange={(_, newValue) => {
                    setSelectedLanguages(newValue.map((v) => v.key));
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.key === value.key
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select languages..." />
                  )}
                  ChipProps={{ size: "small" }}
                  sx={{ width: { xs: "100%", md: "80%", lg: "80%" } }}
                />
              </Box>
            </>
          )}

          {!isPage(routes.onboardingVaSelfServe) && (
            <Box>
              <Typography sx={{ mb: 4, maxWidth: "400px" }}>
                To keep campaigns consistent, it takes at most 7 days for your
                changes to take effect.
              </Typography>
            </Box>
          )}
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedNiche?.status === OrganizationNicheStatus.active && (
              <LoadingButton
                loading={loading}
                disabled={loadingFollowUpQuestions}
                variant="contained"
                color="primary"
                sx={{
                  px: 4,
                }}
                disableElevation
                onClick={() => {
                  saveNiche();
                }}
              >
                Save Changes
              </LoadingButton>
            )}
            {selectedNiche?.status === OrganizationNicheStatus.active && (
              <LoadingButton
                loading={statusLoading}
                disabled={loadingFollowUpQuestions}
                variant="contained"
                color="inherit"
                sx={{
                  backgroundColor: "#F1F2EF",
                  color: "primary.main",
                  px: 4,
                }}
                disableElevation
                onClick={() => {
                  saveNiche(OrganizationNicheStatus.canceled);
                }}
              >
                Pause
              </LoadingButton>
            )}
            {selectedNiche?.status === OrganizationNicheStatus.canceled && (
              <LoadingButton
                loading={statusLoading}
                disabled={loadingFollowUpQuestions}
                variant="contained"
                color="primary"
                sx={{
                  px: 4,
                }}
                disableElevation
                onClick={() => {
                  saveNiche(OrganizationNicheStatus.active);
                }}
              >
                Set Active
              </LoadingButton>
            )}
            <LoadingButton
              loading={deleteLoading}
              disabled={loadingFollowUpQuestions}
              variant="contained"
              color="inherit"
              sx={{
                backgroundColor: "#F1F2EF",
                color: "primary.main",
                px: 4,
              }}
              disableElevation
              onClick={() => {
                setDeleteDialog(true);
              }}
            >
              Delete
            </LoadingButton>
          </Box>
        </Box>
      </Drawer>
      <ConfirmDialog
        header={`⚠️ Warning`}
        subheader={
          <>
            <Typography paragraph>
              Outreach for this niche has stopped.
            </Typography>
            <Typography paragraph>
              Your Virtual Assistant will not send <em>new</em> outreach emails
              for this niche, but <strong>already-scheduled emails</strong> will
              still be delivered as planned. You can resume at any time.
            </Typography>
            <Typography>
              To cancel them, choose <strong>Cancel scheduled VA emails</strong>{" "}
              below. This will cancel emails your VA has scheduled for this
              niche. You can also review them on your{" "}
              <Link component={RouterLink} to={`/${routes.inbox}/scheduled`}>
                Scheduled Emails
              </Link>{" "}
              page.
            </Typography>
          </>
        }
        open={warningDialog}
        handleConfirm={() => {
          setWarningDialog(false);
        }}
        confirmText="Okay"
        handleCancel={cancelScheduledEmails}
        cancelText="Cancel scheduled VA emails"
        loading={cancelEmailsLoading}
      />

      <ConfirmDialog
        header="Are you sure?"
        subheader={
          <>
            <Typography paragraph>
              Deleting this niche will remove all its settings and stop any
              related outreach. Continue?
            </Typography>
          </>
        }
        open={deleteDialog}
        handleConfirm={async () => {
          setDeleteDialog(false);
          await deleteNiche();
        }}
        handleCancel={() => {
          setDeleteDialog(false);
        }}
        handleClose={() => {
          setDeleteDialog(false);
        }}
        confirmText="Yes"
      />
    </>
  );
}
